import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Calendar, Mic, Users, UserPlus, Share2, Phone, LogOut, 
  Save, Plus, Trash2, Edit, Upload, X, Menu, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('schedule');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Data states
  const [schedule, setSchedule] = useState([]);
  const [selectedDay, setSelectedDay] = useState(0);
  const [hosts, setHosts] = useState([]);
  const [guests, setGuests] = useState([]);
  const [podcasts, setPodcasts] = useState([]);
  const [socialLinks, setSocialLinks] = useState({ facebook: '', instagram: '' });
  const [contactInfo, setContactInfo] = useState({
    email: '', phone: '', whatsapp: '',
    melbourneAddress: '', melbournePhone: '',
    sydneyAddress: '', sydneyPhone: ''
  });

  // Edit modals
  const [editingHost, setEditingHost] = useState(null);
  const [editingPodcast, setEditingPodcast] = useState(null);
  const [editingProgram, setEditingProgram] = useState(null);
  
  const fileInputRef = useRef(null);
  const token = sessionStorage.getItem('adminToken');
  const role = sessionStorage.getItem('adminRole') || 'admin';
  const isScheduleOnly = role === 'schedule';

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    // Website editing is admin-only. Planner roles (producer/host) are sent to
    // the Scaletta — they must not be able to edit site content.
    if (role !== 'admin' && role !== 'schedule') {
      navigate('/admin/scaletta', { replace: true });
      return;
    }
    loadAllData();
  }, [token, role, navigate]);

  const apiCall = async (endpoint, method = 'GET', body = null) => {
    const options = {
      method,
      headers: {
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json',
      },
    };
    if (body) {
      options.body = JSON.stringify(body);
    }
    const response = await fetch(`${BACKEND_URL}/api${endpoint}`, options);
    if (response.status === 401) {
      sessionStorage.removeItem('adminAuth');
      navigate('/admin');
      return null;
    }
    return response;
  };

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await fetch(`${BACKEND_URL}/api/admin/upload`, {
      method: 'POST',
      headers: {
        'Authorization': 'Bearer ' + token,
      },
      body: formData,
    });
    
    if (response.ok) {
      const data = await response.json();
      return data.url;
    }
    throw new Error('Upload failed');
  };

  const loadAllData = async () => {
    setLoading(true);
    try {
      // Schedule editors are only permitted on the schedule endpoint, so don't
      // call the others (they'd be forbidden).
      const scheduleRes = await apiCall('/admin/schedule');
      if (scheduleRes?.ok) {
        const data = await scheduleRes.json();
        if (Array.isArray(data) && data.length > 0 && data[0].programs) {
          setSchedule(data);
        } else {
          setSchedule([]);
        }
      }
      if (isScheduleOnly) return;

      const [hostsRes, guestsRes, podcastsRes, socialRes, contactRes] = await Promise.all([
        apiCall('/admin/hosts'),
        apiCall('/admin/guests'),
        apiCall('/admin/podcasts'),
        apiCall('/admin/social'),
        apiCall('/admin/contact'),
      ]);

      if (hostsRes?.ok) {
        const data = await hostsRes.json();
        setHosts(Array.isArray(data) ? data : []);
      }
      if (guestsRes?.ok) {
        const data = await guestsRes.json();
        setGuests(Array.isArray(data) ? data : []);
      }
      if (podcastsRes?.ok) {
        const data = await podcastsRes.json();
        setPodcasts(Array.isArray(data) ? data : []);
      }
      if (socialRes?.ok) {
        const data = await socialRes.json();
        setSocialLinks({ facebook: data.facebook || '', instagram: data.instagram || '' });
      }
      if (contactRes?.ok) {
        const data = await contactRes.json();
        setContactInfo({
          email: data.email || '',
          phone: data.phone || '',
          whatsapp: data.whatsapp || '',
          melbourneAddress: data.melbourneAddress || '',
          melbournePhone: data.melbournePhone || '',
          sydneyAddress: data.sydneyAddress || '',
          sydneyPhone: data.sydneyPhone || ''
        });
      }
    } catch (error) {
      console.error('Error loading data:', error);
      showMessage('error', 'Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminToken');
    sessionStorage.removeItem('adminRole');
    sessionStorage.removeItem('adminUser');
    sessionStorage.removeItem('adminShows');
    navigate('/admin');
  };

  const handleInitAllData = async () => {
    if (!window.confirm('This will initialize all data with defaults. Continue?')) return;
    setLoading(true);
    try {
      const res = await apiCall('/admin/init-all', 'POST');
      if (res?.ok) {
        showMessage('success', 'All data initialized!');
        loadAllData();
      }
    } catch (error) {
      showMessage('error', 'Error initializing data');
    } finally {
      setLoading(false);
    }
  };

  // ============ SCHEDULE FUNCTIONS ============
  const saveScheduleDay = async (dayIndex) => {
    const day = schedule[dayIndex];
    try {
      const res = await apiCall(`/admin/schedule/${day.day}`, 'PUT', day);
      if (res?.ok) {
        showMessage('success', `Schedule for ${day.dayNameEn} saved!`);
      }
    } catch (error) {
      showMessage('error', 'Error saving schedule');
    }
  };

  const addProgram = (dayIndex) => {
    const newSchedule = [...schedule];
    newSchedule[dayIndex].programs.push({
      time: '00:00',
      program: 'New Program',
      host: '',
      image: ''
    });
    setSchedule(newSchedule);
  };

  const updateProgram = (dayIndex, programIndex, field, value) => {
    const newSchedule = [...schedule];
    newSchedule[dayIndex].programs[programIndex][field] = value;
    setSchedule(newSchedule);
  };

  const deleteProgram = (dayIndex, programIndex) => {
    if (!window.confirm('Delete this program?')) return;
    const newSchedule = [...schedule];
    newSchedule[dayIndex].programs.splice(programIndex, 1);
    setSchedule(newSchedule);
  };

  // ============ HOST/GUEST FUNCTIONS ============
  const saveHost = async () => {
    if (!editingHost) return;
    try {
      let res;
      if (editingHost.id && !editingHost.isNew) {
        res = await apiCall(`/admin/hosts/${editingHost.id}`, 'PUT', editingHost);
      } else {
        res = await apiCall('/admin/hosts', 'POST', editingHost);
      }
      if (res?.ok) {
        showMessage('success', 'Saved!');
        setEditingHost(null);
        loadAllData();
      }
    } catch (error) {
      showMessage('error', 'Error saving');
    }
  };

  const deleteHost = async (id) => {
    if (!window.confirm('Delete this person?')) return;
    try {
      const res = await apiCall(`/admin/hosts/${id}`, 'DELETE');
      if (res?.ok) {
        showMessage('success', 'Deleted!');
        loadAllData();
      }
    } catch (error) {
      showMessage('error', 'Error deleting');
    }
  };

  // ============ PODCAST FUNCTIONS ============
  const savePodcast = async () => {
    if (!editingPodcast) return;
    try {
      let res;
      if (editingPodcast.id && !editingPodcast.isNew) {
        res = await apiCall(`/admin/podcasts/${editingPodcast.id}`, 'PUT', editingPodcast);
      } else {
        res = await apiCall('/admin/podcasts', 'POST', editingPodcast);
      }
      if (res?.ok) {
        showMessage('success', 'Podcast saved!');
        setEditingPodcast(null);
        loadAllData();
      }
    } catch (error) {
      showMessage('error', 'Error saving podcast');
    }
  };

  const deletePodcast = async (id) => {
    if (!window.confirm('Delete this podcast?')) return;
    try {
      const res = await apiCall(`/admin/podcasts/${id}`, 'DELETE');
      if (res?.ok) {
        showMessage('success', 'Deleted!');
        loadAllData();
      }
    } catch (error) {
      showMessage('error', 'Error deleting');
    }
  };

  // ============ SETTINGS FUNCTIONS ============
  const saveSocialLinks = async () => {
    try {
      const res = await apiCall('/admin/social', 'PUT', socialLinks);
      if (res?.ok) showMessage('success', 'Social links saved!');
    } catch (error) {
      showMessage('error', 'Error saving');
    }
  };

  const saveContactInfo = async () => {
    try {
      const res = await apiCall('/admin/contact', 'PUT', contactInfo);
      if (res?.ok) showMessage('success', 'Contact info saved!');
    } catch (error) {
      showMessage('error', 'Error saving');
    }
  };

  // ============ IMAGE UPLOAD HANDLER ============
  const handleImageUpload = async (e, callback) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      showMessage('success', 'Uploading...');
      const url = await uploadImage(file);
      callback(url);
      showMessage('success', 'Image uploaded!');
    } catch (error) {
      showMessage('error', 'Upload failed');
    }
  };

  const allTabs = [
    { id: 'schedule', label: 'Schedule', icon: Calendar },
    { id: 'hosts', label: 'Hosts', icon: Users },
    { id: 'guests', label: 'Guests', icon: UserPlus },
    { id: 'podcasts', label: 'Podcasts', icon: Mic },
    { id: 'social', label: 'Social Links', icon: Share2 },
    { id: 'contact', label: 'Contact', icon: Phone },
  ];
  // Schedule editors only ever see the Schedule tab.
  const tabs = isScheduleOnly ? allTabs.filter((t) => t.id === 'schedule') : allTabs;

  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-md sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/rete-italia-logo.png" alt="Rete Italia" className="h-10" />
            <h1 className="text-xl font-bold text-gray-900 hidden md:block">
              {isScheduleOnly ? 'Palinsesto Editor' : 'Admin Dashboard'}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {!isScheduleOnly && (
              <Button onClick={handleInitAllData} variant="outline" size="sm" className="hidden md:flex items-center gap-1">
                <RefreshCw className="w-4 h-4" /> Init Data
              </Button>
            )}
            <Link to="/admin/scaletta" className="text-sm font-semibold text-[#009246] hover:underline hidden md:block px-2">
              Scaletta →
            </Link>
            <Link to="/" target="_blank" className="text-sm text-gray-600 hover:text-[#009246] hidden md:block px-3">
              View Site →
            </Link>
            <Button onClick={handleLogout} variant="outline" size="sm" className="flex items-center gap-2">
              <LogOut className="w-4 h-4" />
            </Button>
            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Message Toast */}
      {message.text && (
        <div className={`fixed top-20 right-4 px-6 py-3 rounded-lg shadow-lg z-50 ${
          message.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
        }`}>
          {message.text}
        </div>
      )}

      <div className="container mx-auto px-4 py-6">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Sidebar */}
          <aside className={`md:w-56 bg-white rounded-xl shadow-md ${mobileMenuOpen ? 'block' : 'hidden md:block'}`}>
            <nav className="p-3 space-y-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => { setActiveTab(tab.id); setMobileMenuOpen(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-sm ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-[#009246] to-[#00b359] text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <tab.icon className="w-5 h-5" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1 bg-white rounded-xl shadow-md p-4 md:p-6 min-h-[600px]">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#009246]"></div>
              </div>
            ) : (
              <>
                {/* ============ SCHEDULE TAB ============ */}
                {activeTab === 'schedule' && (
                  <div>
                    <h2 className="text-2xl font-bold mb-4">Schedule Management</h2>
                    
                    {/* Day Tabs */}
                    <div className="flex flex-wrap gap-2 mb-6">
                      {schedule && schedule.length > 0 && schedule.map((day, idx) => (
                        <button
                          key={day.day || idx}
                          onClick={() => setSelectedDay(idx)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                            selectedDay === idx
                              ? 'bg-[#009246] text-white'
                              : 'bg-gray-100 hover:bg-gray-200'
                          }`}
                        >
                          {day.dayNameEn || day.day}
                        </button>
                      ))}
                    </div>

                    {(!schedule || schedule.length === 0) && (
                      <p className="text-gray-500 text-center py-8">No schedule data. Click "Init Data" to add default schedule.</p>
                    )}

                    {/* Programs for Selected Day */}
                    {schedule[selectedDay] && schedule[selectedDay].programs && (
                      <div className="space-y-3">
                        {schedule[selectedDay].programs.map((program, pIdx) => (
                          <div key={pIdx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <input
                              type="time"
                              value={program.time}
                              onChange={(e) => updateProgram(selectedDay, pIdx, 'time', e.target.value)}
                              className="px-3 py-2 border rounded-lg w-28"
                            />
                            <input
                              type="text"
                              value={program.program}
                              onChange={(e) => updateProgram(selectedDay, pIdx, 'program', e.target.value)}
                              className="flex-1 px-3 py-2 border rounded-lg"
                              placeholder="Program name"
                            />
                            <input
                              type="text"
                              value={program.host}
                              onChange={(e) => updateProgram(selectedDay, pIdx, 'host', e.target.value)}
                              className="w-40 px-3 py-2 border rounded-lg"
                              placeholder="Host name"
                            />
                            {/* Program image: upload / preview / remove */}
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <label className="cursor-pointer" title="Carica immagine programma">
                                {program.image ? (
                                  <img
                                    src={program.image?.startsWith('/api') ? `${BACKEND_URL}${program.image}` : program.image}
                                    alt=""
                                    className="w-11 h-11 rounded object-cover border border-gray-300 hover:opacity-80"
                                  />
                                ) : (
                                  <div className="w-11 h-11 rounded border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:border-[#009246]">
                                    <Upload className="w-4 h-4" />
                                  </div>
                                )}
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={(e) => handleImageUpload(e, (url) => updateProgram(selectedDay, pIdx, 'image', url))}
                                />
                              </label>
                              {program.image && (
                                <button
                                  onClick={() => updateProgram(selectedDay, pIdx, 'image', '')}
                                  title="Rimuovi immagine"
                                  className="p-1 text-gray-400 hover:text-red-500"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                            <button
                              onClick={() => deleteProgram(selectedDay, pIdx)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        ))}
                        
                        <div className="flex gap-3 pt-4">
                          <Button onClick={() => addProgram(selectedDay)} variant="outline" className="flex items-center gap-2">
                            <Plus className="w-4 h-4" /> Add Program
                          </Button>
                          <Button onClick={() => saveScheduleDay(selectedDay)} className="bg-[#009246] flex items-center gap-2">
                            <Save className="w-4 h-4" /> Save {schedule[selectedDay]?.dayNameEn}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ============ HOSTS TAB ============ */}
                {activeTab === 'hosts' && (
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-2xl font-bold">Hosts</h2>
                      <Button 
                        onClick={() => setEditingHost({ name: '', image: '', role: 'host', isNew: true })} 
                        className="bg-[#009246] flex items-center gap-2"
                      >
                        <Plus className="w-4 h-4" /> Add Host
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                      {hosts.map((host) => (
                        <div key={host.id} className="bg-gray-50 rounded-xl p-4 text-center group relative">
                          <img 
                            src={host.image?.startsWith('/api') ? `${BACKEND_URL}${host.image}` : host.image} 
                            alt={host.name} 
                            className="w-20 h-20 rounded-full mx-auto mb-3 object-cover"
                          />
                          <p className="font-medium text-sm">{host.name}</p>
                          <div className="flex justify-center gap-2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => setEditingHost(host)} className="p-1.5 bg-blue-100 text-blue-600 rounded-lg">
                              <Edit className="w-4 h-4" />
                            </button>
                            <button onClick={() => deleteHost(host.id)} className="p-1.5 bg-red-100 text-red-600 rounded-lg">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    {hosts.length === 0 && (
                      <p className="text-gray-500 text-center py-8">No hosts. Click "Init Data" to add defaults.</p>
                    )}
                  </div>
                )}

                {/* ============ GUESTS TAB ============ */}
                {activeTab === 'guests' && (
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-2xl font-bold">Guests</h2>
                      <Button 
                        onClick={() => setEditingHost({ name: '', image: '', role: 'guest', isNew: true })} 
                        className="bg-[#009246] flex items-center gap-2"
                      >
                        <Plus className="w-4 h-4" /> Add Guest
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                      {guests.map((guest) => (
                        <div key={guest.id} className="bg-gray-50 rounded-xl p-4 text-center group relative">
                          <img 
                            src={guest.image?.startsWith('/api') ? `${BACKEND_URL}${guest.image}` : guest.image} 
                            alt={guest.name} 
                            className="w-20 h-20 rounded-full mx-auto mb-3 object-cover"
                          />
                          <p className="font-medium text-sm">{guest.name}</p>
                          <div className="flex justify-center gap-2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => setEditingHost(guest)} className="p-1.5 bg-blue-100 text-blue-600 rounded-lg">
                              <Edit className="w-4 h-4" />
                            </button>
                            <button onClick={() => deleteHost(guest.id)} className="p-1.5 bg-red-100 text-red-600 rounded-lg">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    {guests.length === 0 && (
                      <p className="text-gray-500 text-center py-8">No guests. Click "Init Data" to add defaults.</p>
                    )}
                  </div>
                )}

                {/* ============ PODCASTS TAB ============ */}
                {activeTab === 'podcasts' && (
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-2xl font-bold">Podcasts</h2>
                      <Button 
                        onClick={() => setEditingPodcast({ title: '', description: '', link: '', image: '', episodes: 0, duration: '30 min', isNew: true })} 
                        className="bg-[#009246] flex items-center gap-2"
                      >
                        <Plus className="w-4 h-4" /> Add Podcast
                      </Button>
                    </div>
                    <div className="space-y-3">
                      {podcasts.map((podcast) => (
                        <div key={podcast.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl group">
                          <img 
                            src={podcast.image?.startsWith('/api') ? `${BACKEND_URL}${podcast.image}` : podcast.image} 
                            alt={podcast.title} 
                            className="w-16 h-16 rounded-lg object-cover"
                          />
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold truncate">{podcast.title}</h3>
                            <p className="text-sm text-gray-600 truncate">{podcast.description}</p>
                            <p className="text-xs text-gray-500 mt-1">{podcast.episodes} episodes</p>
                          </div>
                          <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => setEditingPodcast(podcast)} className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                              <Edit className="w-4 h-4" />
                            </button>
                            <button onClick={() => deletePodcast(podcast.id)} className="p-2 bg-red-100 text-red-600 rounded-lg">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    {podcasts.length === 0 && (
                      <p className="text-gray-500 text-center py-8">No podcasts. Click "Init Data" to add defaults.</p>
                    )}
                  </div>
                )}

                {/* ============ SOCIAL LINKS TAB ============ */}
                {activeTab === 'social' && (
                  <div>
                    <h2 className="text-2xl font-bold mb-6">Social Media Links</h2>
                    <div className="space-y-4 max-w-lg">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Facebook URL</label>
                        <input
                          type="url"
                          value={socialLinks.facebook}
                          onChange={(e) => setSocialLinks({ ...socialLinks, facebook: e.target.value })}
                          className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#009246]"
                          placeholder="https://www.facebook.com/..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Instagram URL</label>
                        <input
                          type="url"
                          value={socialLinks.instagram}
                          onChange={(e) => setSocialLinks({ ...socialLinks, instagram: e.target.value })}
                          className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#009246]"
                          placeholder="https://www.instagram.com/..."
                        />
                      </div>
                      <Button onClick={saveSocialLinks} className="bg-[#009246] flex items-center gap-2">
                        <Save className="w-4 h-4" /> Save Changes
                      </Button>
                    </div>
                  </div>
                )}

                {/* ============ CONTACT TAB ============ */}
                {activeTab === 'contact' && (
                  <div>
                    <h2 className="text-2xl font-bold mb-6">Contact Information</h2>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <h3 className="font-semibold text-lg">General</h3>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                          <input
                            type="email"
                            value={contactInfo.email}
                            onChange={(e) => setContactInfo({ ...contactInfo, email: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                          <input
                            type="tel"
                            value={contactInfo.phone}
                            onChange={(e) => setContactInfo({ ...contactInfo, phone: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp</label>
                          <input
                            type="tel"
                            value={contactInfo.whatsapp}
                            onChange={(e) => setContactInfo({ ...contactInfo, whatsapp: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg"
                          />
                        </div>
                      </div>
                      <div className="space-y-4">
                        <h3 className="font-semibold text-lg">Melbourne Office</h3>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                          <textarea
                            value={contactInfo.melbourneAddress}
                            onChange={(e) => setContactInfo({ ...contactInfo, melbourneAddress: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg"
                            rows={2}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                          <input
                            type="tel"
                            value={contactInfo.melbournePhone}
                            onChange={(e) => setContactInfo({ ...contactInfo, melbournePhone: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg"
                          />
                        </div>
                        <h3 className="font-semibold text-lg pt-4">Sydney Office</h3>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                          <textarea
                            value={contactInfo.sydneyAddress}
                            onChange={(e) => setContactInfo({ ...contactInfo, sydneyAddress: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg"
                            rows={2}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                          <input
                            type="tel"
                            value={contactInfo.sydneyPhone}
                            onChange={(e) => setContactInfo({ ...contactInfo, sydneyPhone: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg"
                          />
                        </div>
                      </div>
                    </div>
                    <Button onClick={saveContactInfo} className="bg-[#009246] flex items-center gap-2 mt-6">
                      <Save className="w-4 h-4" /> Save Changes
                    </Button>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>

      {/* ============ EDIT HOST/GUEST MODAL ============ */}
      {editingHost && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">
              {editingHost.isNew ? 'Add' : 'Edit'} {editingHost.role === 'host' ? 'Host' : 'Guest'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name</label>
                <input
                  type="text"
                  value={editingHost.name}
                  onChange={(e) => setEditingHost({ ...editingHost, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Photo</label>
                <div className="flex items-center gap-3">
                  {editingHost.image && (
                    <img 
                      src={editingHost.image?.startsWith('/api') ? `${BACKEND_URL}${editingHost.image}` : editingHost.image} 
                      alt="Preview" 
                      className="w-16 h-16 rounded-full object-cover"
                    />
                  )}
                  <label className="flex-1">
                    <div className="flex items-center gap-2 px-4 py-2 border-2 border-dashed rounded-lg cursor-pointer hover:border-[#009246]">
                      <Upload className="w-5 h-5" />
                      <span>Upload Image</span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleImageUpload(e, (url) => setEditingHost({ ...editingHost, image: url }))}
                    />
                  </label>
                </div>
                <input
                  type="text"
                  value={editingHost.image}
                  onChange={(e) => setEditingHost({ ...editingHost, image: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg mt-2"
                  placeholder="Or enter image URL"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button onClick={() => setEditingHost(null)} variant="outline" className="flex-1">Cancel</Button>
              <Button onClick={saveHost} className="flex-1 bg-[#009246]">Save</Button>
            </div>
          </div>
        </div>
      )}

      {/* ============ EDIT PODCAST MODAL ============ */}
      {editingPodcast && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">
              {editingPodcast.isNew ? 'Add' : 'Edit'} Podcast
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Title</label>
                <input
                  type="text"
                  value={editingPodcast.title}
                  onChange={(e) => setEditingPodcast({ ...editingPodcast, title: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={editingPodcast.description}
                  onChange={(e) => setEditingPodcast({ ...editingPodcast, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Link URL</label>
                <input
                  type="url"
                  value={editingPodcast.link}
                  onChange={(e) => setEditingPodcast({ ...editingPodcast, link: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="https://..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Episodes</label>
                  <input
                    type="number"
                    value={editingPodcast.episodes}
                    onChange={(e) => setEditingPodcast({ ...editingPodcast, episodes: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Duration</label>
                  <input
                    type="text"
                    value={editingPodcast.duration}
                    onChange={(e) => setEditingPodcast({ ...editingPodcast, duration: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="30 min"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Image</label>
                <div className="flex items-center gap-3">
                  {editingPodcast.image && (
                    <img 
                      src={editingPodcast.image?.startsWith('/api') ? `${BACKEND_URL}${editingPodcast.image}` : editingPodcast.image} 
                      alt="Preview" 
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                  )}
                  <label className="flex-1">
                    <div className="flex items-center gap-2 px-4 py-2 border-2 border-dashed rounded-lg cursor-pointer hover:border-[#009246]">
                      <Upload className="w-5 h-5" />
                      <span>Upload Image</span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleImageUpload(e, (url) => setEditingPodcast({ ...editingPodcast, image: url }))}
                    />
                  </label>
                </div>
                <input
                  type="text"
                  value={editingPodcast.image}
                  onChange={(e) => setEditingPodcast({ ...editingPodcast, image: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg mt-2"
                  placeholder="Or enter image URL"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button onClick={() => setEditingPodcast(null)} variant="outline" className="flex-1">Cancel</Button>
              <Button onClick={savePodcast} className="flex-1 bg-[#009246]">Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
